// src/components/games/diceMatch/gameLogic.ts

import {
  GameState,
  GameStateSnapshot,
  GameConfig,
  GridCell,
  DiceType,
  DiceSlot,
  DICE_PROGRESSION,
  DICE_SCORE_VALUES,
  DEFAULT_CONFIG,
  SLOT_GENERATION_WEIGHTS,
} from './types';

/**
 * Generate a die from a specific slot based on its probability weights
 */
function generateDieFromSlot(slot: DiceSlot): DiceType {
  const weights = SLOT_GENERATION_WEIGHTS[slot.slotNumber] || { 'd4': 100 };

  // Calculate total weight
  const totalWeight = Object.values(weights).reduce((sum, weight) => sum + weight, 0);

  // Generate random number between 0 and totalWeight
  let random = Math.random() * totalWeight;

  // Select die type based on weighted probability
  for (const [diceType, weight] of Object.entries(weights)) {
    random -= weight;
    if (random <= 0) {
      return diceType as DiceType;
    }
  }

  // Fallback (should never reach here)
  return 'd4';
}

/**
 * Regenerate dice from all slots
 */
export function regenerateDiceFromSlots(slots: DiceSlot[]): DiceType[] {
  return slots.map(slot => generateDieFromSlot(slot));
}

/**
 * Create initial dice slots
 */
function createInitialSlots(numSlots: number): DiceSlot[] {
  const slots: DiceSlot[] = [];
  for (let i = 1; i <= numSlots; i++) {
    slots.push({ slotNumber: i });
  }
  return slots;
}

/**
 * Create initial game state
 */
export function createInitialGameState(config: GameConfig = DEFAULT_CONFIG): GameState {
  const grid: GridCell[][] = [];

  for (let row = 0; row < config.gridSize; row++) {
    grid[row] = [];
    for (let col = 0; col < config.gridSize; col++) {
      grid[row][col] = {
        diceType: null,
        isLocked: false,
        id: `cell-${row}-${col}`,
      };
    }
  }

  // Create initial slots and generate dice from them
  const diceSlots = createInitialSlots(config.startingSlots);
  const availableDice = regenerateDiceFromSlots(diceSlots);

  return {
    grid,
    score: 0,
    movesRemaining: config.startingMoves,
    diceSlots,
    availableDice,
    inGameCurrency: 0,
    currentRoll: null,
    gameStatus: 'active',
    sparklesCreated: 0,
    currencyMultiplier: config.currencyMultiplier,
    trinketEffects: {
      scoreMultiplier: 1.0,
      rerollsRemaining: 0,
      undoRemaining: 0,
      guaranteedMerges: 0,
      skipDieLevel: false,
      wildDieNext: false,
      doubleDieNext: false,
    },
    moveHistory: [],
  };
}

/**
 * Roll a new die
 */
export function rollDie(gameState: GameState, diceType: DiceType = 'd4'): DiceType {
  // For now, always return the specified dice type
  // Later can add randomness or progression
  return diceType;
}

/**
 * Check if a cell position is valid
 */
function isValidPosition(grid: GridCell[][], row: number, col: number): boolean {
  return row >= 0 && row < grid.length && col >= 0 && col < grid[0].length;
}

/**
 * Get adjacent cells (up, down, left, right)
 */
function getAdjacentCells(
  grid: GridCell[][],
  row: number,
  col: number
): Array<{ row: number; col: number; cell: GridCell }> {
  const adjacent: Array<{ row: number; col: number; cell: GridCell }> = [];
  const directions = [
    [-1, 0], // up
    [1, 0],  // down
    [0, -1], // left
    [0, 1],  // right
  ];

  for (const [dRow, dCol] of directions) {
    const newRow = row + dRow;
    const newCol = col + dCol;

    if (isValidPosition(grid, newRow, newCol)) {
      adjacent.push({
        row: newRow,
        col: newCol,
        cell: grid[newRow][newCol],
      });
    }
  }

  return adjacent;
}

/**
 * Find all possible merges on the grid
 * If placementPosition is provided, ensures the combined die spawns at that position
 */
export function findMerges(
  grid: GridCell[][],
  placementPosition?: { row: number; col: number }
): Array<{
  cells: Array<{ row: number; col: number }>;
  diceType: DiceType;
}> {
  const merges: Array<{
    cells: Array<{ row: number; col: number }>;
    diceType: DiceType;
  }> = [];

  const processed = new Set<string>();

  for (let row = 0; row < grid.length; row++) {
    for (let col = 0; col < grid[row].length; col++) {
      const cell = grid[row][col];

      if (!cell.diceType || cell.isLocked || processed.has(`${row},${col}`)) {
        continue;
      }

      // Check adjacent cells for matches
      const adjacent = getAdjacentCells(grid, row, col);

      for (const adj of adjacent) {
        if (
          adj.cell.diceType === cell.diceType &&
          !adj.cell.isLocked &&
          !processed.has(`${adj.row},${adj.col}`)
        ) {
          // Found a matching pair
          // If placementPosition is specified and matches one of the cells,
          // ensure it becomes cell2 (where the combined die will spawn)
          let cell1Pos = { row, col };
          let cell2Pos = { row: adj.row, col: adj.col };

          if (placementPosition) {
            // Check if placement position matches either cell
            const isCell1Placement = row === placementPosition.row && col === placementPosition.col;
            const isCell2Placement = adj.row === placementPosition.row && adj.col === placementPosition.col;

            if (isCell1Placement) {
              // Swap so placement position becomes cell2
              [cell1Pos, cell2Pos] = [cell2Pos, cell1Pos];
            }
            // If isCell2Placement is true, order is already correct
          }

          merges.push({
            cells: [cell1Pos, cell2Pos],
            diceType: cell.diceType,
          });

          processed.add(`${row},${col}`);
          processed.add(`${adj.row},${adj.col}`);
          break; // Only merge with one adjacent cell at a time
        }
      }
    }
  }

  return merges;
}

/**
 * Get the next dice type in progression
 */
export function getNextDiceType(currentType: DiceType, skipLevel: boolean = false): DiceType | null {
  const currentIndex = DICE_PROGRESSION.indexOf(currentType);
  if (currentIndex === -1 || currentIndex >= DICE_PROGRESSION.length - 1) {
    return null;
  }

  // If skip level is active, skip one level (e.g., d4 -> d8 instead of d6)
  const skip = skipLevel ? 2 : 1;
  const nextIndex = currentIndex + skip;

  if (nextIndex >= DICE_PROGRESSION.length) {
    // If skipping would go past sparkle, just return sparkle
    return DICE_PROGRESSION[DICE_PROGRESSION.length - 1];
  }

  return DICE_PROGRESSION[nextIndex];
}

/**
 * Perform a merge on the grid
 */
export function performMerge(
  gameState: GameState,
  merge: { cells: Array<{ row: number; col: number }>; diceType: DiceType }
): GameState {
  const newGrid = gameState.grid.map(row => row.map(cell => ({ ...cell })));
  const nextDice = getNextDiceType(merge.diceType, gameState.trinketEffects.skipDieLevel);

  if (!nextDice) {
    return gameState;
  }

  // Clear the two merged cells
  const [cell1, cell2] = merge.cells;
  newGrid[cell1.row][cell1.col] = {
    diceType: null,
    isLocked: false,
    id: newGrid[cell1.row][cell1.col].id,
  };

  // Place the upgraded die in the second cell
  // Sparkles always lock the space (they're the highest tier!)
  const shouldLock = nextDice === 'sparkle';

  newGrid[cell2.row][cell2.col] = {
    diceType: nextDice,
    isLocked: shouldLock,
    id: newGrid[cell2.row][cell2.col].id,
  };

  // Calculate score and currency rewards
  const baseScoreGain = DICE_SCORE_VALUES[nextDice];
  const scoreGain = Math.floor(baseScoreGain * gameState.trinketEffects.scoreMultiplier);
  const baseCurrencyGain = Math.floor(scoreGain / 10);
  const currencyGain = Math.floor(baseCurrencyGain * gameState.currencyMultiplier);

  return {
    ...gameState,
    grid: newGrid,
    score: gameState.score + scoreGain,
    inGameCurrency: gameState.inGameCurrency + currencyGain,
    sparklesCreated: nextDice === 'sparkle' ? gameState.sparklesCreated + 1 : gameState.sparklesCreated,
  };
}

/**
 * Process all available merges automatically
 * All merges follow gravity: results spawn at the lower position (higher row number)
 * For horizontal merges, results spawn at the leftmost position (lower col number)
 */
export function processAllMerges(
  gameState: GameState,
  placementPosition?: { row: number; col: number }
): GameState {
  let currentState = { ...gameState };

  let merges = findMerges(currentState.grid);

  while (merges.length > 0) {
    let merge = merges[0];

    // If we have a placement position on first iteration,
    // prioritize the merge involving that position
    if (placementPosition) {
      const pos = placementPosition; // Store for closure
      const placementMerge = merges.find(m =>
        m.cells.some(cell =>
          cell.row === pos.row && cell.col === pos.col
        )
      );
      if (placementMerge) {
        merge = placementMerge;
      }
      // Clear placement position after first merge
      placementPosition = undefined;
    }

    // Apply gravity rule: merged die spawns at lower position
    // cell2 is where the result spawns, so ensure it's the "lower" position
    const [cell1, cell2] = merge.cells;

    // Compare rows first (higher row = lower on screen = gravity target)
    if (cell1.row > cell2.row) {
      // cell1 is lower, swap so result goes to cell1
      merge = { ...merge, cells: [cell2, cell1] };
    } else if (cell1.row === cell2.row) {
      // Horizontal merge - prefer leftmost (lower column)
      if (cell1.col < cell2.col) {
        // cell1 is more left, swap so result goes to cell1
        merge = { ...merge, cells: [cell2, cell1] };
      }
    }
    // If cell2.row > cell1.row, cell2 is already the lower position (correct)

    // Process the merge
    currentState = performMerge(currentState, merge);

    // Find next merges
    merges = findMerges(currentState.grid);
  }

  return currentState;
}

/**
 * Place a die on the grid
 */
export function placeDie(
  gameState: GameState,
  row: number,
  col: number,
  diceType: DiceType
): GameState | null {
  // Validate placement
  const cell = gameState.grid[row]?.[col];
  if (!cell || cell.diceType !== null || cell.isLocked) {
    return null; // Invalid placement
  }

  // Remove the placed die from availableDice
  const diceIndex = gameState.availableDice.indexOf(diceType);
  if (diceIndex === -1) {
    return null; // Die not available
  }
  const newAvailableDice = [...gameState.availableDice];
  newAvailableDice.splice(diceIndex, 1);

  // Create new grid with placed die
  const newGrid = gameState.grid.map((r, rIdx) =>
    r.map((c, cIdx) => {
      if (rIdx === row && cIdx === col) {
        return { ...c, diceType };
      }
      return { ...c };
    })
  );

  let newState: GameState = {
    ...gameState,
    grid: newGrid,
    movesRemaining: gameState.movesRemaining, // Moves no longer decrease
    availableDice: newAvailableDice,
    currentRoll: null,
  };

  // Process any merges that resulted from this placement
  // Pass the placement position so combined dice spawn where the player placed
  newState = processAllMerges(newState, { row, col });

  // Regenerate dice from slots after placement
  newState = {
    ...newState,
    availableDice: regenerateDiceFromSlots(newState.diceSlots),
  };

  // Check if game is over
  newState = checkGameStatus(newState);

  return newState;
}

/**
 * Check if there are any valid moves remaining
 */
function hasValidMoves(grid: GridCell[][]): boolean {
  for (let row = 0; row < grid.length; row++) {
    for (let col = 0; col < grid[row].length; col++) {
      const cell = grid[row][col];
      if (cell.diceType === null && !cell.isLocked) {
        return true; // Found an empty, unlocked cell
      }
    }
  }
  return false;
}

/**
 * Check game status and update if needed
 */
export function checkGameStatus(gameState: GameState): GameState {
  // Check if no dice available
  if (gameState.availableDice.length === 0) {
    return { ...gameState, gameStatus: 'lost' };
  }

  // Check if grid is full and no moves possible
  if (!hasValidMoves(gameState.grid) && gameState.currentRoll === null) {
    return { ...gameState, gameStatus: 'lost' };
  }

  // Game continues until board is full - no move limit!
  return gameState;
}

/**
 * Get a new die from the available dice pool
 * Returns the first available die (or null if none available)
 */
export function getNewDie(gameState: GameState): GameState {
  // Get the first available die from the pool
  const newRoll = gameState.availableDice.length > 0 ? gameState.availableDice[0] : null;

  return {
    ...gameState,
    currentRoll: newRoll,
  };
}

/**
 * Purchase an in-game upgrade
 */
export function purchaseUpgrade(
  gameState: GameState,
  upgradeType: 'slot',
  value: number,
  cost: number
): GameState | null {
  if (gameState.inGameCurrency < cost) {
    return null; // Not enough currency
  }

  const updates: Partial<GameState> = {
    inGameCurrency: gameState.inGameCurrency - cost,
  };

  if (upgradeType === 'slot') {
    const MAX_SLOTS = 18;
    const currentSlotCount = gameState.diceSlots.length;

    if (currentSlotCount >= MAX_SLOTS) {
      // At max slots - upgrade existing slots instead
      // Increase slot numbers for better generation chances
      updates.diceSlots = gameState.diceSlots.map(slot => ({
        slotNumber: slot.slotNumber + 1,
      }));
    } else {
      // Below max - add new slots with sequential slot numbers
      const slotsToAdd = Math.min(value, MAX_SLOTS - currentSlotCount);
      const currentMaxSlot = currentSlotCount;
      const newSlots: DiceSlot[] = [];
      for (let i = 1; i <= slotsToAdd; i++) {
        newSlots.push({ slotNumber: currentMaxSlot + i });
      }
      updates.diceSlots = [...gameState.diceSlots, ...newSlots];
    }

    // Regenerate dice from all slots (with new numbers/additions)
    updates.availableDice = regenerateDiceFromSlots(updates.diceSlots);
  }

  return { ...gameState, ...updates };
}

/**
 * Calculate meta currency earned from sparkles created
 * Meta currency = number of sparkles created
 */
export function calculateMetaCurrency(sparkles: number): number {
  return sparkles;
}

/**
 * Save current state to history (for undo)
 */
export function saveStateSnapshot(gameState: GameState): GameState {
  const snapshot: GameStateSnapshot = {
    grid: gameState.grid.map(row => row.map(cell => ({ ...cell }))),
    score: gameState.score,
    movesRemaining: gameState.movesRemaining,
    diceSlots: gameState.diceSlots.map(slot => ({ ...slot })),
    availableDice: [...gameState.availableDice],
    inGameCurrency: gameState.inGameCurrency,
    currentRoll: gameState.currentRoll,
    sparklesCreated: gameState.sparklesCreated,
    trinketEffects: { ...gameState.trinketEffects },
  };

  // Keep only last 10 moves in history to avoid memory issues
  const newHistory = [...gameState.moveHistory, snapshot].slice(-10);

  return {
    ...gameState,
    moveHistory: newHistory,
  };
}

/**
 * Undo last move
 */
export function undoLastMove(gameState: GameState): GameState | null {
  if (gameState.moveHistory.length === 0) {
    return null; // No history to undo
  }

  const newHistory = [...gameState.moveHistory];
  const previousState = newHistory.pop();

  if (!previousState) {
    return null;
  }

  // Restore previous state but keep moveHistory and trinketEffects.undoRemaining
  return {
    ...gameState,
    grid: previousState.grid,
    score: previousState.score,
    movesRemaining: previousState.movesRemaining,
    diceSlots: previousState.diceSlots,
    availableDice: previousState.availableDice,
    inGameCurrency: previousState.inGameCurrency,
    currentRoll: previousState.currentRoll,
    sparklesCreated: previousState.sparklesCreated,
    moveHistory: newHistory,
    trinketEffects: {
      ...previousState.trinketEffects,
      undoRemaining: gameState.trinketEffects.undoRemaining - 1, // Decrement undo count
    },
  };
}
